/**
 * ambientOverlayState — Phaser-free helpers for the persistent foreground
 * overlay (AGE-1431, painted-transitions phase α).
 *
 * Holds the bits of the overlay that don't need a Phaser context — drift
 * tuning, palette constants, and the reduced-motion query — so they can be
 * unit-tested headless and so the Scene class stays focused on
 * GameObject wiring.
 *
 * See `docs/design/scene-transitions-painted.md` §4.D and §6.
 */

/**
 * Mote count window. The spec calls for ~12–20 motes at any time; we author
 * 16 so reduced-motion still leaves enough on screen to read as ambient
 * texture when their drift slows.
 */
export const AMBIENT_MOTE_COUNT = 16;

/** Lifespan of an individual mote in ms. Long enough that the cluster
 *  reads as continuous drift without abrupt re-spawns. */
export const AMBIENT_MOTE_LIFESPAN_MS = 16_000;

/**
 * Per-mote drift speed (px/s) — slow upward + light horizontal sway.
 * Tuned so a mote crosses the 540px canvas in ~16s.
 */
export const AMBIENT_MOTE_SPEED_X_MIN = -8;
export const AMBIENT_MOTE_SPEED_X_MAX = 8;
export const AMBIENT_MOTE_SPEED_Y_MIN = -28;
export const AMBIENT_MOTE_SPEED_Y_MAX = -14;

/**
 * Per-mote alpha window. The spec asks for 8–18% opacity — quiet enough
 * not to compete with painted plates, present enough to anchor the eye
 * during transitions.
 */
export const AMBIENT_MOTE_ALPHA_MIN = 0.08;
export const AMBIENT_MOTE_ALPHA_MAX = 0.18;

/** Watercolor-paper grain alpha, full canvas. ~5% per the design doc. */
export const AMBIENT_GRAIN_ALPHA = 0.05;

/** Slow horizontal drift of the grain plate (px/s). Subtle parallax that
 *  matches the mote envelope without reading as motion. */
export const AMBIENT_GRAIN_DRIFT_PX_PER_S = 4;

/**
 * Reduced-motion dampening factor. Per the spec the overlay must "slow to
 * 25% speed or freeze." 0.25 keeps a barely-perceptible drift so the
 * texture-match anchor still works without a full freeze.
 */
export const AMBIENT_REDUCED_MOTION_FACTOR = 0.25;

/**
 * Palette tints used for motes (Cream / Pale Linen / Pale Sage variants
 * per art-bible §3.1). Keeping the list short — the overlay is a *texture*,
 * not a focal element, so palette discipline beats variety.
 */
export const AMBIENT_MOTE_TINTS = [
  0xf5e6d0, // Cream
  0xead9c4, // Warm Linen (close to Pale Linen)
  0xc8d4a9, // Pale Sage
] as const;

/** Generic test-injection seam — `globalThis.matchMedia` in real browsers,
 *  a stub in the headless harness. */
export type ReducedMotionQuery = {
  matches: boolean;
  addEventListener?: (
    type: 'change',
    listener: (ev: { matches: boolean }) => void,
  ) => void;
  removeEventListener?: (
    type: 'change',
    listener: (ev: { matches: boolean }) => void,
  ) => void;
};

/** Resolve the global `matchMedia` function in a way that works in both
 *  browsers (where `window.matchMedia === globalThis.matchMedia`) and the
 *  vitest/node harness (where there is no `window`). Returns null when the
 *  function is unavailable. */
function resolveMatchMedia(): ((q: string) => ReducedMotionQuery) | null {
  const mm = (globalThis as unknown as { matchMedia?: (q: string) => ReducedMotionQuery }).matchMedia;
  return typeof mm === 'function' ? mm.bind(globalThis) : null;
}

/**
 * Read the current reduced-motion preference from the browser. Returns
 * `false` in headless / no-window environments so unit tests get a stable
 * default. Wraps the media-query in a try/catch because some embeds (e.g.
 * older Safari, sandboxed iframes) throw on matchMedia.
 */
export function readPrefersReducedMotion(): boolean {
  const mm = resolveMatchMedia();
  if (!mm) return false;
  try {
    return mm('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Resolve the live media-query object so the scene can subscribe to
 * runtime preference changes. Returns null when matchMedia is unavailable.
 */
export function getReducedMotionQuery(): ReducedMotionQuery | null {
  const mm = resolveMatchMedia();
  if (!mm) return null;
  try {
    return mm('(prefers-reduced-motion: reduce)');
  } catch {
    return null;
  }
}

/**
 * Apply the reduced-motion dampening to a base velocity. Used by both
 * the mote emitter (speedX/speedY) and the grain TileSprite (tileScroll).
 */
export function dampenForReducedMotion(value: number, reduced: boolean): number {
  return reduced ? value * AMBIENT_REDUCED_MOTION_FACTOR : value;
}
